"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useVisualViewportHeight } from "@/app/_hooks/use-visual-viewport-height";
import ConversationsSidebar from "./conversations-sidebar";
import type { CoreServiceWithPlans } from "@/app/_lib/db/core-service";
import type { SolutionWithInclude } from "@/app/_lib/db/solutions";
import type { getUserAllFeedbacks } from "../lib/actions";

type FeedbackItem = Awaited<ReturnType<typeof getUserAllFeedbacks>>[0];

interface Promotion {
  id: string;
  title: string;
  description: string;
  tag: string;
  duration: Date | string;
  image: string;
}

interface Props {
  children: React.ReactNode;
  initialFeedbacks: FeedbackItem[];
  userId?: string | null;
  services: Pick<CoreServiceWithPlans, "id" | "title">[];
  solutions: Pick<SolutionWithInclude, "id" | "headTitle">[];
  promotions?: Promotion[];
  basePath?: string;
  showPromotions?: boolean;
  filterType?: "SALES" | "SUPPORT";
}

export default function MessagesShell({
  children,
  initialFeedbacks,
  userId,
  services,
  solutions,
  promotions = [],
  basePath = "/dashboard/messages",
  showPromotions = true,
  filterType,
}: Props) {
  const pathname = usePathname();
  const router = useRouter();
  useVisualViewportHeight();

  const isOnChat =
    pathname !== basePath &&
    pathname !== `${basePath}/new` &&
    pathname.startsWith(`${basePath}/`);

  // True when viewing or starting a conversation (sidebar hides on mobile)
  const isOnConversation = isOnChat || pathname === `${basePath}/new`;

  // While a conversation is open, lock the document from scrolling so that
  // focusing the composer can't push the whole page up behind the keyboard —
  // the chat area sizes itself to var(--vvh) and is the only thing that scrolls.
  useEffect(() => {
    if (!isOnConversation) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOnConversation]);

  // On mobile, when few conversations and on the list page, stack panels vertically
  const hasFewConversations = initialFeedbacks.length <= 2;
  const stackedLayout =
    !isOnConversation && hasFewConversations && showPromotions;

  return (
    <div
      className={`flex ${stackedLayout ? "flex-col md:flex-row" : "flex-row"}`}
      style={{ height: "calc(var(--vvh, 100dvh) - 60px)" }}
    >
      {/* Left sidebar */}
      <div
        className={`${
          isOnConversation ? "hidden md:flex" : "flex"
        } flex-col flex-shrink-0 overflow-hidden ${
          stackedLayout
            ? "w-full md:w-[280px] border-b border-border md:border-b-0 md:border-r max-h-[240px] md:max-h-none"
            : "w-full md:w-[280px] border-r border-border"
        }`}
      >
        <ConversationsSidebar
          initialFeedbacks={initialFeedbacks}
          userId={userId}
          onNewChat={() => router.push(`${basePath}/new`)}
          promotions={promotions}
          basePath={basePath}
          showPromotions={showPromotions}
          filterType={filterType}
        />
      </div>

      {/* Right content */}
      <div
        className={`${
          isOnConversation || stackedLayout ? "flex" : "hidden md:flex"
        } flex-1 flex-col min-h-0 min-w-0`}
      >
        {children}
      </div>
    </div>
  );
}
